<template>
  <div class="attendance" :class="{ compact }">
    <BaseButton
      v-if="disabled"
      kind="auxiliary"
      state="disabled"
      type="secondary"
      :text="label"
    >
      <template #icon>
        <component :is="iconComponent" class="attendance-icon" />
      </template>
    </BaseButton>
    <ButtonMenu v-else :offset="[0, 4]" placement="bottom-start">
      <template #btn>
        <BaseButton kind="auxiliary" type="secondary" :text="label">
          <template #icon>
            <component :is="iconComponent" class="attendance-icon" />
          </template>
        </BaseButton>
      </template>
      <template #content>
        <BaseButton
          ref="activeButtonEl"
          class="full"
          kind="auxiliary"
          text="Active"
          :is-expanded="true"
          :state="value === 'attend' ? 'selected' : 'default'"
          @click="set('attend')"
        >
          <template #right>
            <span
              ref="activeInfoEl"
              class="info-icon"
              @mouseenter="showActiveTooltip"
              @mouseleave="hideActiveTooltip"
            >
              <InfoIcon />
            </span>
          </template>
        </BaseButton>
        <BaseButton
          ref="passiveButtonEl"
          class="full"
          kind="auxiliary"
          text="Passive"
          :is-expanded="true"
          :state="value === 'ignore' ? 'selected' : 'default'"
          @click="set('ignore')"
        >
          <template #right>
            <span
              ref="passiveInfoEl"
              class="info-icon"
              @mouseenter="showPassiveTooltip"
              @mouseleave="hidePassiveTooltip"
            >
              <InfoIcon />
            </span>
          </template>
        </BaseButton>
      </template>
    </ButtonMenu>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
} from "vue";

import type { Attendance } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import CrossedEyeIcon from "@/components/icons/CrossedEyeIcon.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import { useTooltip } from "@/components/tooltip";

export default defineComponent({
  components: { CrossedEyeIcon, InfoIcon, EyeIcon, ButtonMenu, BaseButton },
  props: {
    compact: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    value: {
      type: String as PropType<Attendance>,
      default: "attend",
    },
  },
  emits: {
    "update:value": (_: Attendance) => true,
  },
  setup(props, { emit }) {
    const { value } = toRefs(props);
    const label = computed<string>(() =>
      value.value === "attend" ? "Active" : "Passive"
    );
    const iconComponent = computed(() =>
      value.value === "attend" ? EyeIcon : CrossedEyeIcon
    );
    const set = (v: Attendance) => emit("update:value", v);

    const activeButtonEl = ref<InstanceType<typeof BaseButton>>();
    const activeInfoEl = ref<HTMLElement>();
    const {
      showTooltip: showActiveTooltip,
      hideTooltip: hideActiveTooltip,
      release: releaseActiveTooltip,
    } = useTooltip(
      activeInfoEl,
      ref({
        text: "Active events are displayed in the Agenda's timeline",
        shortcut: undefined,
      }),
      "top"
    );

    const passiveButtonEl = ref<InstanceType<typeof BaseButton>>();
    const passiveInfoEl = ref<HTMLElement>();
    const {
      showTooltip: showPassiveTooltip,
      hideTooltip: hidePassiveTooltip,
      release: releasePassiveTooltip,
    } = useTooltip(
      passiveInfoEl,
      ref({
        text: "Passive events are hidden from the Agenda's timeline",
        shortcut: undefined,
      }),
      "top"
    );
    onBeforeUnmount(() => {
      releaseActiveTooltip();
      releasePassiveTooltip();
    });
    return {
      activeButtonEl,
      activeInfoEl,
      hideActiveTooltip,
      hidePassiveTooltip,
      iconComponent,
      label,
      passiveButtonEl,
      passiveInfoEl,
      set,
      showActiveTooltip,
      showPassiveTooltip,
    };
  },
});
</script>

<style scoped lang="scss">
.attendance {
  display: flex;
  flex-direction: column;

  .attendance-icon {
    width: 12px;
    height: 12px;
  }

  .info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;
    flex-shrink: 0;

    :deep(svg) {
      width: 14px;
      height: 14px;
    }
  }
}
</style>
